import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Auth } from 'aws-amplify';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import * as AuthActions from './auth.actions';
import { selectAuthState } from './auth.selectors';

import { UserService } from '@core/api/user.service';
import { AppState } from '../index';
import * as ParcelActions from '../parcel/parcel.actions';
import * as SiteActions from '../site/site.actions';
import * as MapControlActions from '../map-control/map-control.actions';

@Injectable()
export class AuthEffects {
  constructor(
    readonly actions$: Actions,
    readonly userService: UserService,
    readonly router: Router,
    readonly store: Store<AppState>
  ) {}

  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.login),
      withLatestFrom(this.store.select(selectAuthState)),
      switchMap(([action, authState]) => {
        return forkJoin([of(authState), this.userService.getMe()]);
      }),
      switchMap(([authState, userMe]) => {
        const actions: any[] = [
          AuthActions.loginSuccess({
            user: userMe,
            returnUrl: authState.returnUrl
          })
        ];
        return actions;
      }),
      catchError(error => {
        return of(AuthActions.loginFailure({ error }));
      })
    );
  });

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(action => {
          const currentUser = action.user;
          if (action.returnUrl) {
            // Extra logic to force admin to admin routes in case they come in on the '/' route
            if (currentUser.isWriteAdmin || currentUser.isReadOnlyAdmin) {
              if (action.returnUrl.startsWith('admin') || action.returnUrl.startsWith('/admin')) {
                this.router.navigateByUrl(action.returnUrl, {
                  replaceUrl: true,
                });
              } else {
                this.router.navigate(['admin', 'dashboard', 'site'], {
                  replaceUrl: true,
                });
              }
            } else if (currentUser.isLandowner && !action.returnUrl.startsWith('/account/profile')) {
              this.router.navigateByUrl(action.returnUrl, { replaceUrl: true });
            }
          } else if (currentUser.isWriteAdmin || currentUser.isReadOnlyAdmin) {
            this.router.navigate(['admin', 'dashboard', 'site'], { replaceUrl: true });
          } else if (currentUser.isLandowner) {
            if (currentUser.landowners.length === 0 && (currentUser.userSiteStatus == 'No Sites' || currentUser.userSiteStatus == 'Only Cancelled')) {
              this.router.navigate(['site', 0, 'locate-property', 'select-parcels'], { replaceUrl: true });
            } else {
              this.router.navigate(['account', 'dashboard'], {
                replaceUrl: true,
              });
            }
          } else {
            throw new Error('User was not assigned a role');
          }
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      switchMap(action => {
        Auth.signOut();

        // required for when the user is on the change password page and the login screen needs to refresh
        this.router.navigate(['auth'], {
          replaceUrl: true,                    // don't clutter history
          queryParams: { mode: 'login' }  // unique param to signal reset
        });

        return [
          AuthActions.clearAuthData(),
          SiteActions.clearSiteState(),
          ParcelActions.clearAll(),
          MapControlActions.resetMapControls(),
        ];
      })
    )
  );
}
